/**
 * Client email - layout a 3 colonne (Fase 2):
 * CartelleSidebar | MessaggiList | MessaggioViewer + ComposeDialog.
 *
 * Azioni bidirezionali: apertura messaggio -> PATCH flags letto=true
 * (propagato a IMAP dal backend); toolbar rispondi/inoltra/letto/sposta/
 * archivia/elimina; crea/elimina cartelle.
 *
 * Stato di navigazione (account, cartella, messaggio, pagina, ricerca)
 * nella URL via useUrlState: il back del browser ripristina lista,
 * selezione e filtri (regola UI back).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  PanelBottom,
  PanelRight,
  PanelRightClose,
  PenSquare,
  RefreshCw,
  Settings,
  SlidersHorizontal,
} from 'lucide-react';
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from '@floraltech/ui';
import { useUrlState, useUrlNumberState } from '@lib/useUrlState';
import { useAuth } from '@shell/AuthContext';
import {
  emailFetch,
  applicaEtichettaMessaggio,
  rimuoviEtichettaMessaggio,
  listaEtichette,
  avviaMonitoraggio,
  chiudiMonitoraggio,
  type BatchAzione,
  type BatchEsito,
  BATCH_AZIONE_LABEL,
  type EmailAccount,
  type EmailCartella,
  type EmailEtichetta,
  type EmailMessaggioDetail,
  type EmailMessaggioItem,
  type MessaggiPage,
  type MonitoraggioRef,
} from '../lib/api';
import { MonitoraggioDialog } from '../components/MonitoraggioDialog';
import { CartelleSidebar } from '../components/CartelleSidebar';
import { MessaggiList } from '../components/MessaggiList';
import { MessaggioViewer } from '../components/MessaggioViewer';
import { ComposeDialog, type ComposePrefill } from '../components/ComposeDialog';
import { DividerHandle } from '../components/DividerHandle';
import {
  LISTA_H_DEFAULT,
  LISTA_H_MAX,
  LISTA_H_MIN,
  LISTA_W_DEFAULT,
  LISTA_W_MAX,
  LISTA_W_MIN,
  clampMisura,
  leggiLayout,
  salvaLayout,
} from '../lib/layoutStorage';

/** Corpo quotato per la risposta (prefisso "> " riga per riga). */
function quotaCorpo(m: EmailMessaggioDetail): string {
  const data = m.data_ricezione ? new Date(m.data_ricezione).toLocaleString('it-IT') : '';
  const testo = (m.corpo_testo ?? '').split('\n').map((r) => `> ${r}`).join('\n');
  return `\n\nIl ${data}, ${m.da_nome || m.da_email || ''} ha scritto:\n${testo}`;
}

export function EmailLayout() {
  const navigate = useNavigate();
  const { hasPermesso, reload: reloadAuth, user } = useAuth();
  const [accounts, setAccounts] = useState<EmailAccount[] | null>(null);
  const [cartelle, setCartelle] = useState<EmailCartella[]>([]);
  const [pagina, setPagina] = useState<MessaggiPage | null>(null);
  const [dettaglio, setDettaglio] = useState<EmailMessaggioDetail | null>(null);
  const [loadingLista, setLoadingLista] = useState(false);
  const [loadingDettaglio, setLoadingDettaglio] = useState(false);
  const [syncInCorso, setSyncInCorso] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState<ComposePrefill | null>(null);
  const [avviso, setAvviso] = useState<string | null>(null);
  const [selezione, setSelezione] = useState<Set<string>>(new Set());
  const [etichette, setEtichette] = useState<EmailEtichetta[]>([]);
  const etichetteMap = new Map(etichette.map((e) => [e.id, e]));
  // Messaggio per cui e' aperto il dialogo "monitora risposta" (null = chiuso).
  const [dialogMonitora, setDialogMonitora] = useState<EmailMessaggioItem | null>(null);

  const [paneOverride, setPaneOverride] = useState<string | null>(null);
  const readingPane = paneOverride ?? user?.email_reading_pane ?? 'destra';
  useEffect(() => setPaneOverride(null), [user?.email_reading_pane]);

  const [listaW, setListaW] = useState<number>(() =>
    clampMisura(leggiLayout(user?.id)?.listaW, LISTA_W_MIN, LISTA_W_MAX, LISTA_W_DEFAULT)
  );
  const [listaH, setListaH] = useState<number>(() =>
    clampMisura(leggiLayout(user?.id)?.listaH, LISTA_H_MIN, LISTA_H_MAX, LISTA_H_DEFAULT)
  );

  const [accountId, setAccountId] = useUrlState('account', '');
  const [cartellaId, setCartellaId] = useUrlState('cartella', '');
  const [messaggioId, setMessaggioId] = useUrlState('msg', '');
  const [query, setQuery] = useUrlState('q', '');
  const [page, setPage] = useUrlNumberState('page', 1);
  // Filtro tag trasversale alle cartelle: selezionare un tag azzera la
  // cartella e viceversa (mutua esclusione, vedi selezionaEtichetta e
  // l'onSeleziona di CartelleSidebar piu' sotto).
  const [etichettaId, setEtichettaId] = useUrlState('etichetta', '');

  // La selezione e' effimera: si azzera quando cambia il contesto della lista.
  useEffect(() => {
    setSelezione(new Set());
  }, [accountId, cartellaId, etichettaId, page, query]);

  // Registro etichette (tag) del tenant: caricato una volta all'avvio, usato
  // per risolvere gli id in dato (colore/nome) nella sidebar, nella lista e
  // nel viewer.
  useEffect(() => {
    (async () => {
      setEtichette(await listaEtichette());
    })();
  }, []);

  // -- Account -------------------------------------------------------------
  const caricaAccounts = useCallback(async () => {
    const res = await emailFetch('/account');
    if (!res.ok) {
      setAccounts([]);
      return;
    }
    const lista: EmailAccount[] = await res.json();
    setAccounts(lista);
    return lista;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      const lista = await caricaAccounts();
      if (lista && lista.length > 0 && !lista.some((a) => a.id === accountId)) {
        // preferenza casella predefinita (se ancora visibile), altrimenti la
        // prima della lista (ordinata dal backend: personali prima delle PEC)
        const preferita = lista.find((a) => a.id === user?.email_account_default);
        setAccountId((preferita ?? lista[0]).id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const account = accounts?.find((a) => a.id === accountId) ?? null;
  const accountTipo = account?.tipo;

  // Fase 4 PEC: sola lettura su casella condivisa PEC per chi non ha
  // email.pec_gestione (admin/super_admin hanno tutti i codici da /auth/me).
  const soloLettura = account?.tipo === 'pec' && !hasPermesso('email.pec_gestione');

  // Il monitoraggio ha senso solo sulla posta inviata: e' li' che si aspetta
  // una risposta. Fuori dalla cartella Inviata il tasto destro resta inerte.
  const inCartellaInviata =
    !soloLettura && cartelle.find((c) => c.id === cartellaId)?.ruolo === 'sent';

  // Revoca a caldo: un 403/404 su una chiamata dell'account PEC selezionato
  // segnala che il permesso o l'accesso alla casella condivisa e' stato
  // tolto nel frattempo. Avvisa e ricarica lista account + profilo utente.
  const gestisciEsitoPec = useCallback(
    (res: Response): boolean => {
      if (accountTipo === 'pec' && (res.status === 403 || res.status === 404)) {
        setAvviso("Accesso alla casella PEC non piu' disponibile: permessi aggiornati");
        caricaAccounts();
        reloadAuth();
        return true;
      }
      return false;
    },
    [accountTipo, caricaAccounts, reloadAuth]
  );

  // -- Cartelle --------------------------------------------------------------
  const caricaCartelle = useCallback(async () => {
    if (!accountId) return;
    const res = await emailFetch(`/cartelle?account_id=${accountId}`);
    if (!res.ok) {
      gestisciEsitoPec(res);
      return;
    }
    const lista: EmailCartella[] = await res.json();
    setCartelle(lista);
    // Se un tag e' selezionato il filtro e' trasversale alle cartelle: non
    // forzare la inbox su nessuna cartella selezionata (altrimenti il refresh
    // periodico o una syncOra romperebbero il filtro tag attivo).
    if (!etichettaId && lista.length > 0 && !lista.some((c) => c.id === cartellaId)) {
      const inbox = lista.find((c) => c.ruolo === 'inbox') ?? lista[0];
      setCartellaId(inbox.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, cartellaId, etichettaId, gestisciEsitoPec]);

  useEffect(() => {
    caricaCartelle();
  }, [accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Lista messaggi --------------------------------------------------------
  // Serve una cartella O un tag (filtro trasversale, cartella_id e' opzionale
  // lato API quando etichetta_id e' presente).
  const caricaMessaggi = useCallback(async () => {
    if (!accountId || (!cartellaId && !etichettaId)) return;
    setLoadingLista(true);
    try {
      const params = new URLSearchParams({ account_id: accountId, page: String(page) });
      if (cartellaId) params.set('cartella_id', cartellaId);
      if (etichettaId) params.set('etichetta_id', etichettaId);
      if (query) params.set('q', query);
      const res = await emailFetch(`/messaggi?${params.toString()}`);
      if (res.ok) setPagina(await res.json());
      else gestisciEsitoPec(res);
    } finally {
      setLoadingLista(false);
    }
  }, [accountId, cartellaId, etichettaId, page, query, gestisciEsitoPec]);

  useEffect(() => {
    caricaMessaggi();
  }, [caricaMessaggi]);

  // Auto-refresh 60s: badge account + contatori cartelle; in pausa se la
  // scheda e' nascosta; la lista solo senza multi-selezione attiva (per non
  // far perdere la selezione). Il sync backend gira comunque lato server.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden) return;
      caricaAccounts();
      caricaCartelle();
      if (selezione.size === 0) caricaMessaggi();
    }, 60_000);
    return () => window.clearInterval(id);
  }, [caricaAccounts, caricaCartelle, caricaMessaggi, selezione]);

  const aggiornaTutto = useCallback(async () => {
    await caricaCartelle();
    await caricaMessaggi();
  }, [caricaCartelle, caricaMessaggi]);

  const eseguiBatch = useCallback(
    async (azione: BatchAzione, ids: string[], destId?: string, etichettaBatchId?: string) => {
      if (ids.length === 0) return;
      const nelCestino = cartelle.find((c) => c.id === cartellaId)?.ruolo === 'trash';
      if (
        azione === 'elimina' &&
        nelCestino &&
        !window.confirm(`Eliminare DEFINITIVAMENTE ${ids.length} messaggi dal server?`)
      ) {
        return;
      }
      // filtro tag attivo: cartellaId e' deliberatamente vuoto, quindi nelCestino
      // sopra non intercetta i messaggi gia' nel Cestino presenti nella selezione
      // multi-cartella. Conferma sempre per non eliminare in silenzio (DEC review).
      if (
        azione === 'elimina' &&
        !!etichettaId &&
        !window.confirm(
          "Il filtro tag include messaggi da piu' cartelle: quelli gia' nel Cestino verranno eliminati DEFINITIVAMENTE. Continuare?"
        )
      ) {
        return;
      }
      setAvviso(null);
      let res: Response;
      try {
        res = await emailFetch('/messaggi/batch', {
          method: 'POST',
          body: JSON.stringify({
            azione,
            messaggio_ids: ids,
            cartella_id: destId ?? null,
            etichetta_id: etichettaBatchId ?? null,
          }),
        });
      } catch {
        setAvviso('Errore di rete');
        return; // selezione NON azzerata: l'utente puo' riprovare
      }
      if (!res.ok) {
        if (gestisciEsitoPec(res)) return;
        const body = await res.json().catch(() => null);
        setAvviso(body?.detail ?? `Errore ${res.status}`);
        return;
      }
      const esito: BatchEsito = await res.json();
      setAvviso(
        esito.errori.length === 0
          ? `${esito.ok} messaggi ${BATCH_AZIONE_LABEL[azione]}`
          : `${esito.ok} ok, ${esito.errori.length} falliti`
      );
      if (esito.errori.length > 0) console.debug('batch errori', esito.errori);
      // sposta/archivia/elimina portano il messaggio fuori dalla vista corrente
      // (chiude il dettaglio); letto/non_letto e le azioni etichetta lasciano
      // il messaggio dov'e' (solo un flag/tag cambia, niente navigazione).
      if (
        messaggioId &&
        ids.includes(messaggioId) &&
        azione !== 'letto' &&
        azione !== 'non_letto' &&
        azione !== 'applica_etichetta' &&
        azione !== 'rimuovi_etichetta'
      ) {
        setMessaggioId('');
      }
      // aggiornaTutto() ricarica la lista (righe/chip aggiornati dal server),
      // ma non il dettaglio aperto: se il messaggio aperto e' nel batch di
      // un'azione etichetta, aggiorna anche i suoi chip (stessa logica pura
      // aggiungi_id/rimuovi_id del backend, senza round-trip aggiuntivo).
      if (
        (azione === 'applica_etichetta' || azione === 'rimuovi_etichetta') &&
        messaggioId &&
        ids.includes(messaggioId) &&
        etichettaBatchId
      ) {
        setDettaglio((d) => {
          if (!d || d.id !== messaggioId) return d;
          const correnti = d.etichette ?? [];
          const next =
            azione === 'applica_etichetta'
              ? correnti.includes(etichettaBatchId)
                ? correnti
                : [...correnti, etichettaBatchId]
              : correnti.filter((id) => id !== etichettaBatchId);
          return { ...d, etichette: next };
        });
      }
      setSelezione(new Set());
      await aggiornaTutto();
    },
    [cartelle, cartellaId, etichettaId, messaggioId, aggiornaTutto, setMessaggioId, gestisciEsitoPec]
  );

  // -- Dettaglio + marca letto (propagato a IMAP) ---------------------------
  // Su casella PEC in sola lettura NON si chiama la PATCH flags: il backend
  // risponderebbe 403 (email.pec_gestione richiesto) e i non-letti della PEC
  // restano stato condiviso by design.
  useEffect(() => {
    if (!messaggioId) {
      setDettaglio(null);
      return;
    }
    (async () => {
      setLoadingDettaglio(true);
      try {
        const res = await emailFetch(`/messaggi/${messaggioId}`);
        if (!res.ok) {
          gestisciEsitoPec(res);
          return;
        }
        const det: EmailMessaggioDetail = await res.json();
        if (!det.flag_letto && !soloLettura) {
          // Propagazione bidirezionale: unico punto = PATCH flags
          const resFlag = await emailFetch(`/messaggi/${messaggioId}/flags`, {
            method: 'PATCH',
            body: JSON.stringify({ letto: true }),
          });
          if (resFlag.ok) {
            det.flag_letto = true;
            caricaCartelle();
          } else {
            gestisciEsitoPec(resFlag);
          }
        }
        setDettaglio(det);
        setPagina((p) =>
          p
            ? {
                ...p,
                items: p.items.map((m) =>
                  m.id === det.id ? { ...m, flag_letto: true } : m
                ),
              }
            : p
        );
      } finally {
        setLoadingDettaglio(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messaggioId]);

  // -- Azioni ------------------------------------------------------------
  const syncOra = async () => {
    if (!accountId || syncInCorso) return;
    setSyncInCorso(true);
    try {
      const res = await emailFetch(`/account/${accountId}/sync`, { method: 'POST' });
      if (!res.ok) {
        gestisciEsitoPec(res);
        return;
      }
      await aggiornaTutto();
    } finally {
      setSyncInCorso(false);
    }
  };

  const azioneMessaggio = async (
    m: EmailMessaggioDetail,
    chiama: () => Promise<Response>,
    chiudiDettaglio: boolean
  ) => {
    setAvviso(null);
    const res = await chiama();
    if (!res.ok) {
      if (gestisciEsitoPec(res)) return;
      const body = await res.json().catch(() => null);
      setAvviso(body?.detail ?? `Errore ${res.status}`);
      return;
    }
    if (chiudiDettaglio) setMessaggioId('');
    await aggiornaTutto();
  };

  // -- Etichette (tag) sul messaggio aperto ---------------------------------
  // Entrambi gli endpoint (POST applica / DELETE rimuovi) rispondono con la
  // lista aggiornata degli id etichetta del messaggio: aggiorna dettaglio +
  // riga lista in modo ottimistico dalla RISPOSTA, senza rifare una GET.
  const aggiornaEtichetteMessaggio = async (
    msgId: string,
    chiama: () => Promise<Response>
  ) => {
    setAvviso(null);
    const res = await chiama();
    if (!res.ok) {
      if (gestisciEsitoPec(res)) return;
      const body = await res.json().catch(() => null);
      setAvviso(body?.detail ?? `Errore ${res.status}`);
      return;
    }
    const idsAggiornati: string[] = await res.json();
    setDettaglio((d) => (d && d.id === msgId ? { ...d, etichette: idsAggiornati } : d));
    setPagina((p) =>
      p
        ? {
            ...p,
            items: p.items.map((m) =>
              m.id === msgId ? { ...m, etichette: idsAggiornati } : m
            ),
          }
        : p
    );
  };

  // -- Monitoraggio della risposta -------------------------------------------
  // Anche qui l'endpoint risponde con lo stato aggiornato: si aggiorna la riga
  // in locale, senza refetch, per non perdere posizione e scroll nella lista.
  const aggiornaMonitoraggioLocale = (msgId: string, mon: MonitoraggioRef | null) => {
    setDettaglio((d) => (d && d.id === msgId ? { ...d, monitoraggio: mon } : d));
    setPagina((p) =>
      p
        ? { ...p, items: p.items.map((m) => (m.id === msgId ? { ...m, monitoraggio: mon } : m)) }
        : p
    );
  };

  const confermaMonitoraggio = async (msgId: string, giorni: number) => {
    setAvviso(null);
    setDialogMonitora(null);
    const res = await avviaMonitoraggio(msgId, giorni);
    if (!res.ok) {
      if (gestisciEsitoPec(res)) return;
      const body = await res.json().catch(() => null);
      setAvviso(body?.detail ?? `Errore ${res.status}`);
      return;
    }
    aggiornaMonitoraggioLocale(msgId, await res.json());
  };

  const nonMonitorarePiu = async (messaggio: { id: string; monitoraggio?: MonitoraggioRef | null }) => {
    if (!messaggio.monitoraggio) return;
    setAvviso(null);
    const res = await chiudiMonitoraggio(messaggio.monitoraggio.id);
    if (!res.ok) {
      if (gestisciEsitoPec(res)) return;
      const body = await res.json().catch(() => null);
      setAvviso(body?.detail ?? `Errore ${res.status}`);
      return;
    }
    aggiornaMonitoraggioLocale(messaggio.id, await res.json());
  };

  const applicaEtichetta = (msgId: string, etId: string) =>
    aggiornaEtichetteMessaggio(msgId, () => applicaEtichettaMessaggio(msgId, etId));

  const rimuoviEtichetta = (msgId: string, etId: string) =>
    aggiornaEtichetteMessaggio(msgId, () => rimuoviEtichettaMessaggio(msgId, etId));

  const toggleLetto = (m: EmailMessaggioDetail) =>
    azioneMessaggio(
      m,
      () =>
        emailFetch(`/messaggi/${m.id}/flags`, {
          method: 'PATCH',
          body: JSON.stringify({ letto: !m.flag_letto }),
        }),
      false
    ).then(() =>
      setDettaglio((d) => (d && d.id === m.id ? { ...d, flag_letto: !m.flag_letto } : d))
    );

  const sposta = (m: EmailMessaggioDetail, destId: string) =>
    azioneMessaggio(
      m,
      () =>
        emailFetch(`/messaggi/${m.id}/sposta`, {
          method: 'POST',
          body: JSON.stringify({ cartella_id: destId }),
        }),
      true
    );

  const archivia = (m: EmailMessaggioDetail) =>
    azioneMessaggio(m, () => emailFetch(`/messaggi/${m.id}/archivia`, { method: 'POST' }), true);

  const elimina = (m: EmailMessaggioDetail) => {
    const definitivo = cartelle.find((c) => c.id === cartellaId)?.ruolo === 'trash';
    if (
      definitivo &&
      !window.confirm('Eliminare DEFINITIVAMENTE il messaggio dal server?')
    ) {
      return;
    }
    return azioneMessaggio(
      m,
      () => emailFetch(`/messaggi/${m.id}/elimina`, { method: 'POST' }),
      true
    );
  };

  const rispondi = (m: EmailMessaggioDetail) => {
    setComposePrefill({
      to: m.reply_to || m.da_email || '',
      oggetto: m.oggetto?.startsWith('Re:') ? m.oggetto : `Re: ${m.oggetto ?? ''}`,
      corpo: quotaCorpo(m),
      rispondi_a_messaggio_id: m.id,
    });
    setComposeOpen(true);
  };

  const inoltra = (m: EmailMessaggioDetail) => {
    setComposePrefill({
      oggetto: m.oggetto?.startsWith('Fwd:') ? m.oggetto : `Fwd: ${m.oggetto ?? ''}`,
      corpo: `\n\n---------- Messaggio inoltrato ----------\nDa: ${
        m.da_nome || m.da_email || ''
      }\nOggetto: ${m.oggetto ?? ''}\n\n${m.corpo_testo ?? ''}`,
      inoltra_da_messaggio_id: m.id,
    });
    setComposeOpen(true);
  };

  const creaCartella = async (nome: string): Promise<boolean> => {
    setAvviso(null);
    const res = await emailFetch('/cartelle', {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, nome }),
    });
    if (!res.ok) {
      if (gestisciEsitoPec(res)) return false;
      const body = await res.json().catch(() => null);
      setAvviso(body?.detail ?? `Errore ${res.status}`);
      return false;
    }
    await caricaCartelle();
    return true;
  };

  const eliminaCartella = async (c: EmailCartella) => {
    if (
      !window.confirm(
        `Eliminare la cartella ${c.nome_display ?? c.nome_imap}? I messaggi contenuti verranno rimossi anche dal server.`
      )
    ) {
      return;
    }
    setAvviso(null);
    const res = await emailFetch(`/cartelle/${c.id}`, { method: 'DELETE' });
    if (!res.ok) {
      if (gestisciEsitoPec(res)) return;
      const body = await res.json().catch(() => null);
      setAvviso(body?.detail ?? `Errore ${res.status}`);
      return;
    }
    if (cartellaId === c.id) setCartellaId('');
    await aggiornaTutto();
  };

  // Filtro tag trasversale: selezionare un tag azzera la cartella (e
  // viceversa, vedi onSeleziona di CartelleSidebar nel render).
  const selezionaEtichetta = (id: string) => {
    setEtichettaId(id);
    setCartellaId('');
    setMessaggioId('');
    setPage(1);
  };

  const cambiaReadingPane = async (mode: string) => {
    if (mode === readingPane) return;
    setPaneOverride(mode); // ottimistico
    try {
      const token = localStorage.getItem('token') ?? '';
      const res = await fetch('/api/v1/auth/me', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email_reading_pane: mode }),
      });
      if (res.ok) {
        await reloadAuth();
      } else {
        setPaneOverride(null);
      }
    } catch {
      setPaneOverride(null);
    }
  };

  // -- Render ------------------------------------------------------------
  if (accounts === null) {
    return <p className="p-6 text-sm text-muted-foreground">Caricamento...</p>;
  }

  if (accounts.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-10 text-center">
        <Mail className="h-12 w-12 text-muted-foreground" />
        <div>
          <h2 className="text-lg font-semibold">Nessuna casella collegata</h2>
          <p className="text-sm text-muted-foreground">
            Collega la tua casella email per iniziare a usare la posta nell'ERP.
          </p>
        </div>
        <Button onClick={() => navigate('/email/impostazioni')}>
          Collega una casella
        </Button>
      </div>
    );
  }

  const altriNonLetti = (accounts ?? [])
    .filter((a) => a.id !== accountId)
    .reduce((s, a) => s + (a.non_letti || 0), 0);
  const dettaglioAltri = (accounts ?? [])
    .filter((a) => a.id !== accountId && a.non_letti > 0)
    .map((a) => `${a.indirizzo}: ${a.non_letti}`)
    .join(', ');

  const pannelloCartelle = (
    <div className="w-[220px] shrink-0 overflow-y-auto border-r">
      <CartelleSidebar
        cartelle={cartelle}
        selezionataId={cartellaId || null}
        onSeleziona={(c) => {
          setCartellaId(c.id);
          setEtichettaId('');
          setMessaggioId('');
          setPage(1);
        }}
        onCrea={creaCartella}
        onEliminaCartella={eliminaCartella}
        onDropMessaggi={(c, ids) => eseguiBatch('sposta', ids, c.id)}
        soloLettura={soloLettura}
        etichette={etichette}
        etichettaSelezionataId={etichettaId || null}
        onSelezionaEtichetta={selezionaEtichetta}
      />
    </div>
  );
  const pannelloLista = (
    <MessaggiList
      pagina={pagina}
      loading={loadingLista}
      selezionatoId={messaggioId || null}
      query={query}
      selezione={selezione}
      destinazioni={cartelle.filter((c) => c.id !== cartellaId)}
      onQuery={(q) => {
        setQuery(q);
        setPage(1);
      }}
      onSeleziona={(id) => setMessaggioId(id)}
      onPagina={(p) => setPage(p)}
      onSelezione={setSelezione}
      onBatch={(azione, ids, destId, etId) => eseguiBatch(azione, ids, destId, etId)}
      soloLettura={soloLettura}
      etichette={etichetteMap}
      onMonitora={inCartellaInviata ? (m) => setDialogMonitora(m) : undefined}
      onNonMonitorare={inCartellaInviata ? nonMonitorarePiu : undefined}
    />
  );
  const pannelloViewer = (
    <MessaggioViewer
      messaggio={dettaglio}
      loading={loadingDettaglio}
      cartelle={cartelle}
      cartellaCorrenteId={cartellaId || null}
      onRispondi={rispondi}
      onInoltra={inoltra}
      onToggleLetto={toggleLetto}
      onSposta={sposta}
      onArchivia={archivia}
      onElimina={elimina}
      soloLettura={soloLettura}
      etichette={etichetteMap}
      onRimuoviEtichetta={
        soloLettura ? undefined : (etId) => rimuoviEtichetta(messaggioId, etId)
      }
      onApplicaEtichetta={
        soloLettura ? undefined : (etId) => applicaEtichetta(messaggioId, etId)
      }
    />
  );

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col">
      <div className="flex items-center justify-between border-b pb-2">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-semibold">Email</h1>
          {accounts.length > 1 ? (
            <div className="flex items-center gap-1.5">
              <Select
                value={accountId}
                onValueChange={(v) => {
                  setAccountId(v);
                  setCartellaId('');
                  setMessaggioId('');
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-auto min-w-[220px] text-sm" aria-label="Casella email">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center gap-2">
                        <span>{a.indirizzo}</span>
                        {a.tipo === 'pec' && <Badge variant="secondary">PEC</Badge>}
                        {a.non_letti > 0 && (
                          <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                            {a.non_letti}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {altriNonLetti > 0 && (
                <Tooltip label={`Non letti nelle altre caselle - ${dettaglioAltri}`}>
                  <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                    +{altriNonLetti}
                  </span>
                </Tooltip>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">{account?.indirizzo}</span>
          )}
          {account?.tipo === 'pec' && <Badge variant="secondary">PEC</Badge>}
          {soloLettura && (
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Sola lettura
            </span>
          )}
          {account?.stato === 'errore_auth' && (
            <span className="rounded bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
              Autenticazione fallita: riconnetti la casella dalle impostazioni
            </span>
          )}
          {account?.stato === 'errore_temporaneo' && (
            <span className="rounded bg-warning/10 px-2 py-0.5 text-xs text-warning">
              Server di posta non raggiungibile: il sistema ritenta da solo
            </span>
          )}
          {avviso && (
            <span className="rounded bg-warning/10 px-2 py-0.5 text-xs text-warning">
              {avviso}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!soloLettura && (
            <Button variant="outline" size="sm" onClick={syncOra} disabled={syncInCorso}>
              <RefreshCw className={`mr-1 h-4 w-4 ${syncInCorso ? 'animate-spin' : ''}`} />
              {syncInCorso ? 'Sincronizzo...' : 'Sincronizza'}
            </Button>
          )}
          {!soloLettura && (
            <Button
              size="sm"
              onClick={() => {
                setComposePrefill(null);
                setComposeOpen(true);
              }}
            >
              <PenSquare className="mr-1 h-4 w-4" />
              Scrivi
            </Button>
          )}
          <div className="flex items-center rounded-md border" role="group" aria-label="Disposizione riquadro di lettura">
            {(
              [
                { mode: 'destra', Icona: PanelRight, titolo: 'Lettura a destra' },
                { mode: 'sotto', Icona: PanelBottom, titolo: 'Lettura sotto' },
                { mode: 'nascosto', Icona: PanelRightClose, titolo: 'Lettura nascosta' },
              ] as const
            ).map(({ mode, Icona, titolo }) => (
              <Tooltip key={mode} label={titolo}>
                <Button
                  variant={readingPane === mode ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => cambiaReadingPane(mode)}
                  aria-label={titolo}
                  aria-pressed={readingPane === mode}
                >
                  <Icona className="h-4 w-4" />
                </Button>
              </Tooltip>
            ))}
          </div>
          <Tooltip label="Regole email">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/email/regole')}
              aria-label="Regole email"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
          </Tooltip>
          <Tooltip label="Impostazioni email">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/email/impostazioni')}
              aria-label="Impostazioni email"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </Tooltip>
        </div>
      </div>

      {readingPane === 'destra' && (
        <div className="flex min-h-0 flex-1">
          {pannelloCartelle}
          {/* niente shrink-0: su finestre piu' strette di listaW la lista si
              comprime invece di spingere la barra fuori viewport */}
          <div className="min-h-0 min-w-0" style={{ width: listaW }}>
            {pannelloLista}
          </div>
          <DividerHandle
            orientamento="verticale"
            valore={listaW}
            min={LISTA_W_MIN}
            max={LISTA_W_MAX}
            onChange={(v) => {
              setListaW(v);
              salvaLayout(user?.id, { listaW: v });
            }}
            onReset={() => {
              setListaW(LISTA_W_DEFAULT);
              salvaLayout(user?.id, { listaW: LISTA_W_DEFAULT });
            }}
            label="Ridimensiona elenco messaggi"
          />
          {/* min-w: sotto compressione il viewer resta leggibile invece di
              collassare a 0 (flex-basis 0 non partecipa allo shrink) */}
          <div className="min-h-0 min-w-[280px] flex-1 overflow-y-auto">{pannelloViewer}</div>
        </div>
      )}
      {readingPane === 'sotto' && (
        <div className="flex min-h-0 flex-1">
          {pannelloCartelle}
          <div className="flex min-h-0 flex-1 flex-col">
            {/* niente shrink-0: su finestre piu' basse di listaH la lista si
                comprime e la barra di paginazione resta nel viewport */}
            <div className="min-h-0" style={{ height: listaH }}>
              {pannelloLista}
            </div>
            <DividerHandle
              orientamento="orizzontale"
              valore={listaH}
              min={LISTA_H_MIN}
              max={LISTA_H_MAX}
              onChange={(v) => {
                setListaH(v);
                salvaLayout(user?.id, { listaH: v });
              }}
              onReset={() => {
                setListaH(LISTA_H_DEFAULT);
                salvaLayout(user?.id, { listaH: LISTA_H_DEFAULT });
              }}
              label="Ridimensiona elenco messaggi"
            />
            {/* min-h: sotto compressione il viewer resta leggibile invece di
                collassare a 0 (flex-basis 0 non partecipa allo shrink) */}
            <div className="min-h-[160px] flex-1 overflow-y-auto">{pannelloViewer}</div>
          </div>
        </div>
      )}
      {readingPane === 'nascosto' && (
        <div className="flex min-h-0 flex-1">
          {pannelloCartelle}
          {messaggioId ? (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="border-b px-2 py-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMessaggioId('')}
                  aria-label="Torna all'elenco"
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Elenco
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto">{pannelloViewer}</div>
            </div>
          ) : (
            <div className="min-h-0 flex-1">{pannelloLista}</div>
          )}
        </div>
      )}

      {accountId && (
        <ComposeDialog
          accountId={accountId}
          open={composeOpen}
          prefill={composePrefill}
          onOpenChange={setComposeOpen}
          onInviato={aggiornaTutto}
        />
      )}

      {dialogMonitora && (
        <MonitoraggioDialog
          messaggio={dialogMonitora}
          onConferma={(giorni) => confermaMonitoraggio(dialogMonitora.id, giorni)}
          onAnnulla={() => setDialogMonitora(null)}
        />
      )}
    </div>
  );
}
